"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KonfektionierungPriceCalculator = void 0;
const echain_shared_quantities_1 = require("@igus/echain-shared-quantities");
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
const echain_shared_validation_1 = require("@igus/echain-shared-validation");
const echain_shared_configurations_1 = require("@igus/echain-shared-configurations");
const echain_shared_articles_oo_1 = require("@igus/echain-shared-articles-oo");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
const KonfektionierungDelegator_1 = require("./KonfektionierungDelegator");
const KonfektionierungValidator_1 = require("./KonfektionierungValidator");
/**
 * The ConfigurationKonfektionierungCalculator calculates and sets the konfektionierung cost
 * properties of a Configuration.
 *
 * @see https://igusdev.atlassian.net/wiki/spaces/EKE10/pages/44404940/Berechnung+Montagekosten+ReadyChain
 */
class KonfektionierungPriceCalculator {
    /**
     * Calculates all Konfektionierung prices for all countries and currencies for a configuration.
     *
     * @param configuration The `Configuration` to calculate the prices for.
     * @param konfektionierungenRef The reference on the `KonfektionierungList` of the given
     * configuration. These objects will be updated with the calculated prices.
     * @returns The Validation list populated during calculations.
     */
    static calcAndUpdateAll(configuration, konfektionierungenRef) {
        const validation = echain_shared_validation_1.Validation.empty();
        // clear all previously calculated values first
        konfektionierungenRef.clearCalculatedPrices();
        // let delegator decide which konfektionierungen are required
        // afterwards, calculate and update these
        new KonfektionierungDelegator_1.KonfektionierungDelegator()
            .getList(konfektionierungenRef, configuration)
            .toArray()
            .forEach((konfektionierungRef) => {
            // map konfektionierung to calculation and model updater method
            switch (konfektionierungRef.typ) {
                case echain_shared_articles_1.KonfektionierungTyp.leitungEinlegenMeterFactorDefault:
                case echain_shared_articles_1.KonfektionierungTyp.leitungEinlegenMeterFactorChainClosed:
                case echain_shared_articles_1.KonfektionierungTyp.leitungEinlegenMeterFactorChainWithFachboeden:
                case echain_shared_articles_1.KonfektionierungTyp.leitungEinlegenMeterFactorChainWithTrennstegen:
                    this.calcAndUpdateLeitungEinlegen(validation, configuration, konfektionierungRef);
                    break;
                case echain_shared_articles_1.KonfektionierungTyp.schliessenOeffnungsstegFactorRohr:
                case echain_shared_articles_1.KonfektionierungTyp.schliessenOeffnungsstegFactorKette:
                    this.calcAndUpdateOeffnungsstegSchliessen(validation, configuration, konfektionierungRef);
                    break;
                case echain_shared_articles_1.KonfektionierungTyp.BeschriftungLeitungFactorLessThan_4:
                case echain_shared_articles_1.KonfektionierungTyp.BeschriftungLeitungFactorMoreThan_3:
                    this.calcAndUpdateLeitungBeschriften(validation, configuration, konfektionierungRef);
                    break;
                case echain_shared_articles_1.KonfektionierungTyp.befestigungZugentlastungFactor:
                    this.calcAndUpdateZugentlastungMontieren(validation, configuration, konfektionierungRef);
                    break;
                case echain_shared_articles_1.KonfektionierungTyp.auftrommelnTransportgestellAnschrauben:
                    this.calcAndUpdateTrommelAnschrauben(validation, configuration, konfektionierungRef);
                    break;
                case echain_shared_articles_1.KonfektionierungTyp.auftrommelnRatePerMeter:
                case echain_shared_articles_1.KonfektionierungTyp.paletteRatePerMeter:
                    this.calcAndUpdateVerpackungAuflegen(validation, configuration, konfektionierungRef);
                    break;
                case echain_shared_articles_1.KonfektionierungTyp.verpackungTrommelUnder1501:
                case echain_shared_articles_1.KonfektionierungTyp.verpackungTrommelGreater1500:
                case echain_shared_articles_1.KonfektionierungTyp.verpackungPaletteUnder901:
                case echain_shared_articles_1.KonfektionierungTyp.verpackungPaletteOver900:
                    this.calcAndUpdateVerpackungVerpacken(validation, configuration, konfektionierungRef);
                    break;
                case echain_shared_articles_1.KonfektionierungTyp.verpackungMaterialPalette:
                case echain_shared_articles_1.KonfektionierungTyp.verpackungMaterialTrommel:
                    this.calcAndUpdateVerpackungMaterial(validation, configuration, konfektionierungRef);
                    break;
            }
        });
        return validation;
    }
    /**
     * Calculate the price for "laying a Leitung inside a Kette".
     */
    static calcAndUpdateLeitungEinlegen(v, configuration, konfRef) {
        const unitPriceFactors = [];
        const { chainLength, chainArtNr } = this.getChainLengthAndArtNr(configuration);
        if (chainLength) {
            // for every leitung
            configuration
                .getLeitungenClone()
                .filterForBom({ withPlaceholder: false })
                .toArray()
                .forEach((leitung) => {
                var _a, _b, _c, _d;
                // validate
                const validation = KonfektionierungValidator_1.KonfektionierungValidator.calcAndUpdateLeitungEinlegenPossible(chainLength, chainArtNr, leitung);
                if (validation.hasIssues()) {
                    return v.add(validation);
                }
                // calculate
                const ueberstandFestpunkt_m = (_b = (_a = leitung.ueberstandFestpunkt) === null || _a === void 0 ? void 0 : _a.m) !== null && _b !== void 0 ? _b : 0;
                const ueberstandMitnehmer_m = (_d = (_c = leitung.ueberstandMitnehmer) === null || _c === void 0 ? void 0 : _c.m) !== null && _d !== void 0 ? _d : 0;
                const leitungsLaenge = echain_shared_quantities_1.Length.m(ueberstandFestpunkt_m + ueberstandMitnehmer_m + chainLength.m);
                unitPriceFactors.push((chainLength === null || chainLength === void 0 ? void 0 : chainLength.m) + (leitungsLaenge.m - (chainLength === null || chainLength === void 0 ? void 0 : chainLength.m)) / 2);
                return undefined;
            });
        }
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithDifferentUnits(unitPriceFactors, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithDifferentUnits(unitPriceFactors, konfRef.basePriceEK);
    }
    /**
     * Calculate the "closing of a Oeffnungssteg of a Kette"
     */
    static calcAndUpdateOeffnungsstegSchliessen(v, configuration, konfRef) {
        const configModel = configuration.toModel();
        let halbstegig;
        let numGlieder;
        let geschlossen = false;
        let chainArtNr;
        const { type, config } = (0, echain_shared_configurations_1.asTypedConfigurationUnion)(configModel);
        switch (type) {
            case 'Empty': // no e-chain nothing to close ;-)
            case 'Espool': // no harnessing calculation for e-spool
            case 'EspoolChild':
                return v;
            case 'Kette': {
                const kette = (0, echain_shared_articles_oo_1.A)(config.Kette);
                geschlossen = kette.geschlossen;
                halbstegig = kette.halbstegig;
                numGlieder = kette.numGlieder;
                chainArtNr = kette.getQuoteArtNr() || '';
                break;
            }
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
        // validate
        const validation = KonfektionierungValidator_1.KonfektionierungValidator.calcAndUpdateOeffnungsstegSchliessenPossible({
            halbstegig,
            numGlieder,
            geschlossen,
            chainArtNr,
        });
        if (validation.hasIssues()) {
            return v.add(validation);
        }
        // calculate
        numGlieder !== null && numGlieder !== void 0 ? numGlieder : (numGlieder = 0);
        const unitCount = !geschlossen && halbstegig ? numGlieder / 2 : numGlieder;
        const unitPriceFactor = halbstegig ? 0.5 : 1;
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithEqualUnits(unitCount, unitPriceFactor, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithEqualUnits(unitCount, unitPriceFactor, konfRef.basePriceEK);
        return v;
    }
    /**
     * Calculates the price for "labeling the Leitungen"
     */
    static calcAndUpdateLeitungBeschriften(v, configuration, konfRef) {
        // calculate
        const unitCount = configuration
            .getLeitungenClone()
            .filterForBom({ withPlaceholder: false })
            .toArray()
            .filter((leitung) => leitung.beschriftung != null && leitung.beschriftung !== echain_shared_articles_1.LeitungBeschriftung.keine).length;
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithEqualUnits(unitCount, 1, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithEqualUnits(unitCount, 1, konfRef.basePriceEK);
    }
    /**
     * Calculates the price for "assembly of the Zugentlastungen"
     */
    static calcAndUpdateZugentlastungMontieren(v, configuration, konfRef) {
        // validate (without returning.. we just ignore the missing data here to enable invented
        // Zugentlastung articles)
        v.add(KonfektionierungValidator_1.KonfektionierungValidator.calcAndUpdateZugentlastungMontierenPossible(configuration));
        // calculate
        const zugentlastungCountInfo = configuration.getCountOfLeitungenWithZugentlastung();
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithEqualUnits(zugentlastungCountInfo.count, 1, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithEqualUnits(zugentlastungCountInfo.count, 1, konfRef.basePriceEK);
    }
    /**
     * Calculates the price for "mounting the Trommel at a transport device"
     */
    static calcAndUpdateTrommelAnschrauben(v, configuration, konfRef) {
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithEqualUnits(1, 1, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithEqualUnits(1, 1, konfRef.basePriceEK);
    }
    /**
     * Calculates the price for "mounting the Kette at a Verpackung"
     */
    static calcAndUpdateVerpackungAuflegen(v, configuration, konfRef) {
        const leitungen = configuration.getLeitungenClone().filterForBom({ withPlaceholder: false });
        const { chainLength, chainArtNr } = this.getChainLengthAndArtNr(configuration);
        // validate
        const validation = KonfektionierungValidator_1.KonfektionierungValidator.calcAndUpdateVerpackungAuflegenPossible(chainLength, chainArtNr);
        if (validation.hasIssues()) {
            return v.add(validation);
        }
        // calculate
        let unitPriceFactor;
        const summeUeberstaendeMitnehmer = leitungen.getUeberstandSummeMitnehmer();
        const summeUeberstaendeFestpunkt = leitungen.getUeberstandSummeFestpunkt();
        const leitungCount = leitungen.count();
        const ueberstand = leitungCount !== 0 ? (summeUeberstaendeFestpunkt.m + summeUeberstaendeMitnehmer.m) / (leitungCount * 2) : 0;
        if (chainLength) {
            unitPriceFactor = chainLength.m + ueberstand;
        }
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithEqualUnits(1, unitPriceFactor, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithEqualUnits(1, unitPriceFactor, konfRef.basePriceEK);
        return v;
    }
    static calcAndUpdateVerpackungVerpacken(v, configuration, konfRef) {
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithEqualUnits(1, 1, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithEqualUnits(1, 1, konfRef.basePriceEK);
    }
    /**
     * Calculate the price for the required material of a Verpackung.
     */
    static calcAndUpdateVerpackungMaterial(v, configuration, konfRef) {
        // update model
        konfRef.calculatedPriceVK = this.createCalculatedPriceWithEqualUnits(1, 1, konfRef.basePriceVK);
        konfRef.calculatedPriceEK = this.createCalculatedPriceWithEqualUnits(1, 1, konfRef.basePriceEK);
    }
    /**
     * Helper method to abstract all the country/currency stuff away from calculations.
     * It will also take care that all objects are initialized properly.
     *
     * @param unitCount The number of units to calc the total price for.
     * @param unitPriceFactor The factor for every single unit.
     * @param baseCountryPrice The base price for the calculation.
     * @returns The created CalculatedPriceVK
     */
    static createCalculatedPriceWithEqualUnits(unitCount, unitPriceFactor, baseCountryPrice) {
        const calculatedPrice = {
            count: unitCount,
            unitPrice: { Countries: {} },
            totalPrice: { Countries: {} },
        };
        if (unitPriceFactor) {
            // multiply factors with basePrices per country and currency
            for (const country in baseCountryPrice === null || baseCountryPrice === void 0 ? void 0 : baseCountryPrice.Countries) {
                calculatedPrice.unitPrice.Countries[country] = {};
                calculatedPrice.totalPrice.Countries[country] = {};
                for (const currency in baseCountryPrice.Countries[country]) {
                    const basePrice = baseCountryPrice.Countries[country][currency];
                    calculatedPrice.unitPrice.Countries[country][currency] = unitPriceFactor * basePrice;
                    calculatedPrice.totalPrice.Countries[country][currency] = unitPriceFactor * basePrice * unitCount;
                }
            }
        }
        return calculatedPrice;
    }
    /**
     * Helper method to abstract all the country/currency stuff away from calculations.
     * It will also take care that all objects are initialized properly.
     *
     * @param unitPriceFactors The price factor for each single unit to add to the total price.
     * @param baseCountryPrice The base price for the calculation.
     * @returns The created CalculatedPriceVK
     */
    static createCalculatedPriceWithDifferentUnits(unitPriceFactors, baseCountryPrice) {
        const calculatedPrice = {
            count: unitPriceFactors.length,
            unitPrice: undefined,
            totalPrice: { Countries: {} },
        };
        // multiply factors with basePrices per country, currency and unit
        for (const country in baseCountryPrice === null || baseCountryPrice === void 0 ? void 0 : baseCountryPrice.Countries) {
            calculatedPrice.totalPrice.Countries[country] = {};
            for (const currency in baseCountryPrice.Countries[country]) {
                calculatedPrice.totalPrice.Countries[country][currency] = 0;
                const basePrice = baseCountryPrice.Countries[country][currency];
                unitPriceFactors.forEach((unitPriceFactor) => {
                    // calc total price
                    calculatedPrice.totalPrice.Countries[country][currency] += unitPriceFactor * basePrice;
                });
            }
        }
        return calculatedPrice;
    }
    static getChainLengthAndArtNr(configuration) {
        const { type, config } = (0, echain_shared_configurations_1.asTypedConfigurationUnion)(configuration.toModel());
        switch (type) {
            case 'Empty':
            case 'Espool':
            case 'EspoolChild':
                return { chainLength: echain_shared_quantities_1.Length.mm(0), chainArtNr: '' };
            case 'Kette': {
                const kette = (0, echain_shared_articles_oo_1.A)(config.Kette);
                return {
                    chainLength: kette.getMostPreciseEffektiveLaenge() || echain_shared_quantities_1.Length.mm(0),
                    chainArtNr: kette.getQuoteArtNr() || '',
                };
            }
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
    }
}
exports.KonfektionierungPriceCalculator = KonfektionierungPriceCalculator;
//# sourceMappingURL=KonfektionierungPriceCalculator.js.map